import {
  CheckCircleFilled,
  ClockCircleFilled,
  ExclamationCircleFilled,
  QuestionCircleFilled
} from '@ant-design/icons'
import { colors, PROFILE_PICTURE_PLACEHOLDER } from '@lmc/common/constants'
import { GroupedResidentsMultiSelect, Icon, PageHeader } from '@lmc/ui-kit'
import { Select, Spin } from 'antd'
import dayjs from 'dayjs'
import type React from 'react'
import { useMemo, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import styled from 'styled-components'
import { AntdDateFilter } from '../../../../components/generic'
import Legend from '../../../../screens/Residents/screens/ResidentEmarV2Screens/EMARResidentScreenV2/Legend'
import { useMedicationRoundsReport } from '../../hooks/useMedicationRoundsReport'
import type {
  EmarResidentRound,
  EmarRound
} from '../../utils/emarMedicationRoundsTypes'

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  due: '#8c8c8c',
  signed: '#4caf50',
  notSigned: '#e53935',
  notAdministered: '#e6a817',
  prn: '#0958d9'
}

const STATUS_LABELS: Record<string, string> = {
  due: 'Due',
  signed: 'Signed',
  notSigned: 'Not Signed',
  notAdministered: 'Not Admin',
  prn: 'PRN'
}

const STATUS_OPTIONS = [
  { label: 'Due', value: 'due' },
  { label: 'Signed', value: 'signed' },
  { label: 'Not Signed', value: 'notSigned' },
  { label: 'Not Administered', value: 'notAdministered' },
  { label: 'PRN', value: 'prn' }
]

// ─── StatusBadge ─────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: string
  count: number
  hideZero?: boolean
}

const STATUS_BADGE_ICON_STYLE = { fontSize: 20, marginRight: 4 }

const STATUS_BADGE_ICONS: Partial<Record<string, React.ReactElement>> = {
  due: (
    <ClockCircleFilled
      style={{ ...STATUS_BADGE_ICON_STYLE, color: STATUS_COLORS.due }}
    />
  ),
  signed: (
    <CheckCircleFilled
      style={{ ...STATUS_BADGE_ICON_STYLE, color: STATUS_COLORS.signed }}
    />
  ),
  notSigned: (
    <ExclamationCircleFilled
      style={{ ...STATUS_BADGE_ICON_STYLE, color: STATUS_COLORS.notSigned }}
    />
  ),
  notAdministered: (
    <ExclamationCircleFilled
      style={{
        ...STATUS_BADGE_ICON_STYLE,
        color: STATUS_COLORS.notAdministered
      }}
    />
  ),
  prn: (
    <QuestionCircleFilled
      style={{ ...STATUS_BADGE_ICON_STYLE, color: STATUS_COLORS.prn }}
    />
  )
}

const StatusBadge = ({ status, count, hideZero = true }: StatusBadgeProps) => {
  if (hideZero && count === 0) return null
  const icon = STATUS_BADGE_ICONS[status] ?? null

  return (
    <BadgeWrapper>
      {icon}
      <BadgeLabel>
        <strong>{count}</strong> {STATUS_LABELS[status]}
      </BadgeLabel>
    </BadgeWrapper>
  )
}

// ─── ResidentCard ─────────────────────────────────────────────────────────────

const ResidentCard = ({
  entry,
  isOverdue
}: {
  entry: EmarResidentRound
  isOverdue: boolean
}) => {
  const { resident, signed, meds, additionalComment, loggedBy } = entry
  const fullName = `${resident.name.first} ${resident.name.last}`

  return (
    <Card>
      <CardHeader>
        <ResidentAvatar
          src={resident.picture ?? PROFILE_PICTURE_PLACEHOLDER}
          alt={fullName}
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).src =
              PROFILE_PICTURE_PLACEHOLDER
          }}
        />
        <CardNameBlock>
          <ResidentLink href={`/residents/${resident.id}/emar`}>
            {fullName}
            {resident.preferredName ? ` (${resident.preferredName})` : ''}
          </ResidentLink>
          {signed === true ? (
            <SignedBadge $signed>Signed</SignedBadge>
          ) : signed === false ? (
            <SignedBadge $signed={false}>Not signed</SignedBadge>
          ) : null}
        </CardNameBlock>
      </CardHeader>

      <MedList>
        {meds.map((med, i) => (
          <MedItem key={med.logId ?? i}>
            {med.PRN && <PrnLabel>PRN</PrnLabel>}
            <span>{med.medicationName}</span>{' '}
            {med.taken === true && (
              <MedStatus $color={STATUS_COLORS.signed}>(Given)</MedStatus>
            )}
            {med.taken === false && (
              <MedStatus $color={STATUS_COLORS.notAdministered}>
                (Not administered{med.reasonCode ? `: ${med.reasonCode}` : ''})
              </MedStatus>
            )}
            {med.taken === null && med.PRN && (
              <MedStatus $color={STATUS_COLORS.prn}>(Available)</MedStatus>
            )}
            {med.taken === null && !med.PRN && (
              <MedStatus
                $color={isOverdue ? STATUS_COLORS.notSigned : STATUS_COLORS.due}
              >
                {isOverdue ? '(Not signed)' : '(Due)'}
              </MedStatus>
            )}
          </MedItem>
        ))}
      </MedList>

      {additionalComment && (
        <CommentText>Note: {additionalComment}</CommentText>
      )}
      {loggedBy && <SignedByText>Signed by {loggedBy.name}</SignedByText>}
    </Card>
  )
}

const showBadge = (status: string, statusFilter: string[]) =>
  statusFilter.length === 0 || statusFilter.includes(status)

// ─── RoundRow ─────────────────────────────────────────────────────────────────

interface RoundRowProps {
  round: EmarRound
  filteredResidents: EmarResidentRound[]
  isOverdue: boolean
  residentFilter: string[]
  statusFilter: string[]
}

const RoundRow = ({
  round,
  filteredResidents,
  isOverdue,
  residentFilter,
  statusFilter
}: RoundRowProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { time } = round
  const hasResidents = filteredResidents.length > 0

  const hasScheduled = round.residents.length > 0
  const scheduledMeds = filteredResidents.flatMap((r) =>
    r.meds.filter((m) => !m.PRN)
  )
  const prnCount = filteredResidents.flatMap((r) =>
    r.meds.filter((m) => m.PRN && m.taken === true)
  ).length
  const signedCount = scheduledMeds.filter((m) => m.taken === true).length
  const pendingCount = scheduledMeds.filter((m) => m.taken === null).length
  const notAdminCount = scheduledMeds.filter((m) => m.taken === false).length
  // dueCount excludes missed residents to match the due status filter predicate
  const dueCount = isOverdue
    ? 0
    : filteredResidents
        .filter((r) => !r.missed)
        .flatMap((r) => r.meds.filter((m) => !m.PRN))
        .filter((m) => m.taken === null).length
  const notSignedCount = isOverdue ? pendingCount : 0

  return (
    <RowContainer>
      <RowHeader
        $clickable={hasResidents}
        onClick={() => hasResidents && setIsOpen((o) => !o)}
      >
        <TimeCell>{time}</TimeCell>
        <BadgesCell>
          {!hasResidents ? (
            <NothingScheduled>
              {!hasScheduled
                ? 'Nothing scheduled'
                : statusFilter.length > 0 && residentFilter.length === 0
                  ? 'No residents match the selected status'
                  : residentFilter.length > 0 && statusFilter.length === 0
                    ? 'Nothing scheduled for selected residents'
                    : 'No residents match the selected filters'}
            </NothingScheduled>
          ) : (
            <>
              {showBadge('due', statusFilter) && (
                <StatusBadge status="due" count={dueCount} />
              )}
              {showBadge('signed', statusFilter) && (
                <StatusBadge status="signed" count={signedCount} />
              )}
              {showBadge('notSigned', statusFilter) && (
                <StatusBadge status="notSigned" count={notSignedCount} />
              )}
              {showBadge('notAdministered', statusFilter) && (
                <StatusBadge status="notAdministered" count={notAdminCount} />
              )}
              {showBadge('prn', statusFilter) && (
                <StatusBadge status="prn" count={prnCount} />
              )}
            </>
          )}
        </BadgesCell>
        <RightCell>
          {hasResidents ? (
            <Icon
              name={isOpen ? 'UpOutlined' : 'DownOutlined'}
              style={{ fontSize: 11 }}
            />
          ) : null}
        </RightCell>
      </RowHeader>
      <AnimateHeight animateOpacity duration={200} height={isOpen ? 'auto' : 0}>
        <ResidentsGrid>
          {filteredResidents.map((entry) => (
            <ResidentCard
              key={entry.resident.id}
              entry={entry}
              isOverdue={isOverdue}
            />
          ))}
        </ResidentsGrid>
      </AnimateHeight>
    </RowContainer>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const residentMatchesStatus = (
  entry: EmarResidentRound,
  statuses: string[],
  isOverdue: boolean
): boolean => {
  if (statuses.length === 0) return true
  return statuses.some((s) => {
    if (s === 'signed') return entry.signed === true
    if (s === 'notSigned')
      return (
        !entry.signed &&
        isOverdue &&
        entry.meds.some((m) => !m.PRN && m.taken === null)
      )
    if (s === 'due')
      return (
        !entry.signed &&
        !entry.missed &&
        !isOverdue &&
        entry.meds.some((m) => !m.PRN && m.taken === null)
      )
    if (s === 'notAdministered')
      return entry.meds.some((m) => m.taken === false)
    if (s === 'prn')
      return entry.meds.some((m) => m.PRN === true && m.taken === true)
    return false
  })
}

const applyFilters = (
  rounds: EmarRound[],
  residentFilter: string[],
  statusFilter: string[]
): Array<{ round: EmarRound; filteredResidents: EmarResidentRound[] }> => {
  return rounds.map((round) => {
    const isOverdue = round.isOverdue
    let residents = round.residents ?? []
    if (residentFilter.length > 0) {
      residents = residents.filter((r) =>
        residentFilter.includes(r.resident.id)
      )
    }
    if (statusFilter.length > 0) {
      residents = residents.filter((r) =>
        residentMatchesStatus(r, statusFilter, isOverdue)
      )
    }
    return { round, filteredResidents: residents }
  })
}

// ─── Main component ───────────────────────────────────────────────────────────

export const EmarMedicationRoundsReport = () => {
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [residentFilter, setResidentFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  const { data, isLoading, isFetching } = useMedicationRoundsReport(date)
  const rounds = useMemo(() => data?.rounds ?? [], [data])
  const prnAvailableTotal = data?.prnAvailableTotal ?? 0

  const residentsInReport = useMemo(() => {
    const seen = new Map<string, any>()
    for (const round of rounds) {
      for (const r of round.residents) {
        if (!seen.has(r.resident.id)) {
          seen.set(r.resident.id, {
            id: r.resident.id,
            name: r.resident.name,
            picture: r.resident.picture,
            preferredName: r.resident.preferredName,
            status: 'active'
          })
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      `${a.name.first} ${a.name.last}`.localeCompare(
        `${b.name.first} ${b.name.last}`
      )
    )
  }, [rounds])

  const filtered = useMemo(
    () => applyFilters(rounds, residentFilter, statusFilter),
    [rounds, residentFilter, statusFilter]
  )

  const dailyTotals = useMemo(() => {
    let signed = 0,
      due = 0,
      notSigned = 0,
      notAdministered = 0,
      prn = 0
    for (const round of rounds) {
      const overdue = round.isOverdue
      for (const r of round.residents) {
        for (const med of r.meds) {
          if (med.PRN) {
            if (med.taken === true) prn++
          } else if (med.taken === true) {
            signed++
          } else if (med.taken === false) {
            notAdministered++
          } else if (med.taken === null) {
            if (overdue) notSigned++
            else due++
          }
        }
      }
    }
    return { signed, due, notSigned, notAdministered, prn }
  }, [rounds])

  return (
    <ScreenBody>
      <PageHeader title="Medication Rounds Report" />
      <FiltersCard>
        <FilterRow>
          <AntdDateFilter
            currentDate={dayjs(date)}
            setCurrentDate={(d: dayjs.Dayjs) => setDate(d.format('YYYY-MM-DD'))}
            wrapFormItem={false}
          />
          <GroupedResidentsMultiSelect
            residents={residentsInReport}
            title="Residents"
            values={residentFilter}
            handleInputChange={(selected) => setResidentFilter(selected ?? [])}
            style={{ minWidth: 200 }}
          />
          <Select
            mode="multiple"
            placeholder="All statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
            style={{ minWidth: 180 }}
            allowClear
            maxTagCount="responsive"
          />
          <LegendWrapper>
            <Legend />
          </LegendWrapper>
          {!isLoading && isFetching && <Spin size="small" />}
        </FilterRow>
      </FiltersCard>

      {isLoading ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : filtered.length === 0 ? (
        <EmptyState>No rounds configured for this date.</EmptyState>
      ) : (
        <RoundsList>
          {filtered.map(({ round, filteredResidents }) => (
            <RoundRow
              key={round.emarRoundTimingId}
              round={round}
              filteredResidents={filteredResidents}
              isOverdue={round.isOverdue}
              residentFilter={residentFilter}
              statusFilter={statusFilter}
            />
          ))}
          <DailyTotalRow>
            <DailyTotalLabel>
              Daily{'\n'}Total
              {(residentFilter.length > 0 || statusFilter.length > 0) && (
                <DailyTotalSubLabel>full day</DailyTotalSubLabel>
              )}
            </DailyTotalLabel>
            <BadgesCell>
              <StatusBadge
                status="signed"
                count={dailyTotals.signed}
                hideZero={false}
              />
              <StatusBadge
                status="due"
                count={dailyTotals.due}
                hideZero={false}
              />
              <StatusBadge
                status="notSigned"
                count={dailyTotals.notSigned}
                hideZero={false}
              />
              <StatusBadge
                status="notAdministered"
                count={dailyTotals.notAdministered}
                hideZero={false}
              />
              <DailyPrnRatio>
                <QuestionCircleFilled
                  style={{
                    ...STATUS_BADGE_ICON_STYLE,
                    color: STATUS_COLORS.prn
                  }}
                />
                <strong>
                  {dailyTotals.prn}/{prnAvailableTotal}
                </strong>{' '}
                PRN
              </DailyPrnRatio>
            </BadgesCell>
          </DailyTotalRow>
        </RoundsList>
      )}
      <HelpLink
        href="https://support.logmycare.co.uk/en/collections/2749236-emar"
        target="_blank"
        rel="noreferrer"
      >
        ? Help
      </HelpLink>
    </ScreenBody>
  )
}

export default EmarMedicationRoundsReport

// ─── Styled components ────────────────────────────────────────────────────────

const ScreenBody = styled.div`
  padding: 24px 24px 50px;
  max-width: 1128px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FiltersCard = styled.div`
  border: 1px solid ${({ theme }) => theme?.color?.neutral?.color7 ?? '#e8e8e8'};
  border-radius: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme?.color?.neutral?.color10 ?? '#ffffff'};
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`

const LegendWrapper = styled.div`
  margin-left: auto;
`

const BadgeWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
`

const BadgeLabel = styled.span`
  color: #444;
  white-space: nowrap;
`

const NothingScheduled = styled.span`
  font-size: 13px;
  color: #bbb;
  font-style: italic;
`

const RoundsList = styled.div`
  border-top: 1px solid ${colors.grey};
`

const DailyTotalRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 16px;
  gap: 16px;
  border-top: 2px solid ${colors.grey};
  background: ${({ theme }) => theme?.color?.neutral?.color9 ?? '#f5f5f5'};
`

const DailyPrnRatio = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #444;
  white-space: nowrap;
`

const DailyTotalLabel = styled.span`
  font-weight: 700;
  font-size: 13px;
  width: 80px;
  flex-shrink: 0;
  white-space: pre-line;
  line-height: 1.3;
`

const DailyTotalSubLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #999;
`

const HelpLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${colors.darkblue ?? '#1976d2'};
  text-decoration: none;
  margin-top: -8px;

  &:hover {
    text-decoration: underline;
  }
`

const RowContainer = styled.div`
  border-bottom: 1px solid ${colors.grey};
`

const RowHeader = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 0 16px;
  gap: 16px;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$clickable ? colors.lightgrey : 'transparent')};
  }
`

const TimeCell = styled.span`
  font-weight: 700;
  font-size: 15px;
  width: 80px;
  flex-shrink: 0;
`

const BadgesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  flex: 1;
`

const RightCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`

const ResidentsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px 16px;
  background: #fafafa;
`

const Card = styled.div`
  background: #fff;
  border: 1px solid ${colors.grey};
  border-radius: 6px;
  padding: 12px 14px;
  min-width: 260px;
  max-width: 340px;
  flex: 1 1 260px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`

const ResidentAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #eee;
`

const CardNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ResidentLink = styled.a`
  font-weight: 600;
  font-size: 14px;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${colors.darkblue ?? '#1976d2'};
  }
`

const SignedBadge = styled.span<{ $signed: boolean }>`
  display: inline-block;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${(p) => (p.$signed ? '#e8f5e9' : '#fce4ec')};
  color: ${(p) => (p.$signed ? STATUS_COLORS.signed : STATUS_COLORS.notSigned)};
  font-weight: 500;
  width: fit-content;
`

const MedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const MedItem = styled.li`
  font-size: 13px;
  line-height: 1.4;
`

const PrnLabel = styled.strong`
  margin-right: 4px;
  font-size: 11px;
  color: ${STATUS_COLORS.prn};
`

const MedStatus = styled.span<{ $color: string }>`
  font-size: 12px;
  color: ${(p) => p.$color};
`

const CommentText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #666;
  font-style: italic;
`

const SignedByText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #888;
`

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`

const EmptyState = styled.p`
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 15px;
`
